import type {BuiltZoomOption, ClassNames, DisplayConfig, PageElement, QueryMatch, ReaderOptions, UIElements, WheelDirection, ZoomType} from "@/modules/pdf/types";
import {getDocument, GlobalWorkerOptions, type PageViewport, type PDFDocumentProxy, type PDFPageProxy, TextLayer} from "pdfjs-dist";
import AppTheme, {type Theme} from "@/modules/theme";
import ReaderUtils from "@/modules/pdf/utils";

export class PDFReader {
    private abortController?: AbortController;

    private displayConfig: Required<DisplayConfig>;
    private readonly uiElements: UIElements;
    private options: ReaderOptions;

    private pdf: PDFDocumentProxy | null;
    private currentPage: number;
    private currentScale: number;
    private zoomMode: ZoomType;
    private fileBlob: Blob | null;

    private pageElements: Map<number, PageElement>;
    private pageObserver: IntersectionObserver | null;

    private textCache: Map<number, string>;
    private matches: QueryMatch[];
    private currentMatch: number;
    private currentQuery: string;
    private fileName: string;

    private isScrolling: boolean;
    private scrollRaf: number | null;

    private root: HTMLElement;

    private classNames: ClassNames = {
        thumbnail: {
            container: 'thumbnails__container',
            item: 'thumbnail__page__item',
            current: 'current__page',
            pageNumber: 'thumbnail__page__number',
        },

        pages: {
            pageItem: 'page__item',
            textLayer: 'text__layers'
        },

        search: {
            match: 'matched__search__text'
        }
    }

    private localStorageKey = 'reader__engine__theme';

    constructor(options: ReaderOptions) {
        this.options = options;

        this.root = document.querySelector('html');
        this.uiElements = this.options.uiElements || ReaderUtils.getUIElements();
        if (!this.uiElements.engine) throw new Error('uiElements.engine not found');

        const defaultConfig = ReaderUtils.DefaultDisplayConfig;
        const customDisplayConfig = this.options.displayConfig;

        if (!customDisplayConfig) this.displayConfig = defaultConfig;
        else this.displayConfig = {
            zoomStep: customDisplayConfig.zoomStep || defaultConfig.zoomStep,
            minZoom: customDisplayConfig.minZoom || defaultConfig.minZoom,
            maxZoom: customDisplayConfig.maxZoom || defaultConfig.maxZoom,
            devicePixelRatio: customDisplayConfig.devicePixelRatio || defaultConfig.devicePixelRatio,
            thumbnailWidth: customDisplayConfig.thumbnailWidth || defaultConfig.thumbnailWidth
        };

        this.pdf = null;
        this.currentPage = 1;
        this.currentScale = 1;
        this.zoomMode = ReaderUtils.zoom.options.types.auto;
        this.fileBlob = null;

        this.pageElements = new Map();
        this.pageObserver = null;

        this.textCache = new Map();
        this.matches = [];
        this.currentMatch = -1;
        this.currentQuery = '';

        this.isScrolling = false;
        this.scrollRaf = null;

        this.fileName = '';

        GlobalWorkerOptions.workerSrc = this.options.workerSrc;
        this.setTheme();
        this.buildZoomDropdown();
        this.attachEvents();
    }

    toggleLoader = (render: boolean) => {
        const wrapper = this.uiElements.engine
        render ? wrapper.classList.add('rendering') : wrapper.classList.remove('rendering')
    };

    loadFromUrl = async (url: string, fileName: string) => {
        this.toggleLoader(true);
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Failed: ${response.status}`);
        const blob = await response.blob();
        await this.loadFromBlob(blob);
        this.fileName = fileName;

        this.uiElements.name.container.classList.add('loaded');
        this.uiElements.name.renderers.forEach((el) => el.textContent = this.fileName);
    };

    loadFromBlob = async (blob: Blob) => {
        this.toggleLoader(true);
        this.fileBlob = blob;
        const arrayBuffer = await blob.arrayBuffer();
        const loadingTask = getDocument({data: arrayBuffer});
        this.pdf = await loadingTask.promise;

        this.currentPage = 1;
        this.textCache.clear();
        this.pageElements.clear();
        this.clearSearch();

        this.uiElements.paginator.total.forEach((el) => {
            el.textContent = String(this.pdf.numPages).padStart(2, '0');
        })

        const readingZonePaginatorToal = this.uiElements.paginator.container.querySelector<HTMLElement>('[data-paginator="total"]');
        this.uiElements.paginator.container.style.setProperty('--item-min-w', `${readingZonePaginatorToal?.clientWidth}px`);

        this.uiElements.paginator.input.forEach((el) => {
            el.max = String(this.pdf.numPages);
            el.min = '1';
        })

        this.updateCurrentPageUiElements(1);

        await this.buildPagesLayout();
        this.setupIntersectionObserver();
        this.setupScrollListener();
        await this.renderThumbnails();
        this.toggleLoader(false);
    };

    // Create empty page placeholders with correct dimensions
    private buildPagesLayout = async () => {
        this.uiElements.containers.pages.innerHTML = '';
        this.pageElements.clear();

        for (let pageIndex = 1; pageIndex <= this.pdf.numPages; pageIndex++) {
            const page = await this.pdf.getPage(pageIndex);
            const scale = this.computeScale(page);
            const viewport = page.getViewport({scale});

            const wrapper = document.createElement('div');
            wrapper.className = this.classNames.pages.pageItem;
            wrapper.dataset.page = String(pageIndex);
            wrapper.style.width = `${viewport.width}px`;
            wrapper.style.height = `${viewport.height}px`;

            const canvas = document.createElement('canvas');
            const textLayer = document.createElement('div');
            textLayer.className = this.classNames.pages.textLayer;

            wrapper.appendChild(canvas);
            wrapper.appendChild(textLayer);
            this.uiElements.containers.pages.appendChild(wrapper);

            this.pageElements.set(pageIndex, {
                wrapper,
                canvas,
                textLayer,
                rendered: false,
                scale,
                viewport,
                page,
            });
        }
    };

    private buildZoomDropdown = () => {
        // const staticOptions: BuiltZoomOption[] = ReaderUtils.zoom.options.static.presets;
        const staticOptions: BuiltZoomOption[] = ReaderUtils.zoom.options.static.custom(this.options.labels?.zoom || {});

        const dynamicOptions: BuiltZoomOption[] = ReaderUtils.zoom.build({
            minZoom: this.displayConfig.minZoom,
            maxZoom: this.displayConfig.maxZoom,
            zoomStep: this.displayConfig.zoomStep
        })

        this.uiElements.zoom.picker.input.innerHTML = [...staticOptions, ...dynamicOptions].map((option) => {
            return `<option data-display="${option.displayLabel || option.label}" value="${option.key}" ${option.selected ? 'selected' : ''}>${option.label}</option>`
        }).join('');

        this.setZoomPickerDisplayOption();
    }

    // ----------------------------------------------------------
    // Lazy rendering via IntersectionObserver
    // ----------------------------------------------------------
    private setupIntersectionObserver = () => {
        this.pageObserver?.disconnect();

        this.pageObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const pageIndex = Number((entry.target as HTMLElement).dataset.page);
                    this.renderPageIfNeeded(pageIndex).then(r => r);
                }
            });
        }, {
            root: this.uiElements.containers.readingZone,
            rootMargin: '200px 0px', // preload pages slightly off-screen
        });

        this.pageElements.forEach(({wrapper}) => {
            this.pageObserver.observe(wrapper);
        });
    };

    private renderPageIfNeeded = async (pageIndex: number) => {
        const entry = this.pageElements.get(pageIndex);
        if (!entry || entry.rendered) return;
        entry.rendered = true;

        const {canvas, textLayer, page, viewport} = entry;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const DPR = this.displayConfig.devicePixelRatio;

        canvas.width = Math.floor(viewport.width * DPR);
        canvas.height = Math.floor(viewport.height * DPR);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;

        const transform = DPR !== 1 ? [DPR, 0, 0, DPR, 0, 0] : undefined;

        try {
            await page.render({canvas, canvasContext: ctx, viewport, transform}).promise;
            await this.renderTextLayer(textLayer, page, viewport);
            if (this.currentQuery) this.highlightMatchesOnPage(pageIndex);
        } catch (err) {
            entry.rendered = false; // allow retry
            this.toggleLoader(false);
            this.options.onRenderError(err);
        }
    };

    private renderTextLayer = async (layer: HTMLElement, page: PDFPageProxy, viewport: PageViewport) => {
        try {

            if (!page || !layer || !viewport) {
                console.warn('failed rendering text layer | reader.ts => renderTextLayer');
                return;
            }

            layer.innerHTML = '';
            layer.style.width = `${viewport.width}px`;
            layer.style.height = `${viewport.height}px`;

            const textContent = await page.getTextContent();
            const textLayer = new TextLayer({
                textContentSource: textContent,
                container: layer,
                viewport,
            });
            await textLayer.render();
        } catch (e) {
            this.options.onRenderError(e)
        }
    };

    // ----------------------------------------------------------
    // Scroll tracking — detect current visible page
    // ----------------------------------------------------------
    private setupScrollListener = () => {
        this.uiElements.containers.readingZone.addEventListener('scroll', () => {
            if (this.isScrolling) return;
            if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
            this.scrollRaf = requestAnimationFrame(() => this.updateCurrentPageFromScroll());
        });
    };

    private updateCurrentPageFromScroll = () => {
        const container = this.uiElements.containers.readingZone;
        const containerTop = container.scrollTop;
        const containerMid = containerTop + container.clientHeight / 2;

        let closestPage = 1;
        let closestDistance = Infinity;

        this.pageElements.forEach(({wrapper}, pageIndex) => {
            const pageMid = wrapper.offsetTop + wrapper.offsetHeight / 2;
            const distance = Math.abs(pageMid - containerMid);
            if (distance < closestDistance) {
                closestDistance = distance;
                closestPage = pageIndex;
            }
        });

        if (closestPage !== this.currentPage) {
            this.currentPage = closestPage;
            this.updateCurrentPageUiElements(closestPage);
            this.updateActiveThumbnail();
        }
    };

    private updateCurrentPageUiElements = (value: number, pad: boolean = true) => {
        const padLength = pad ? Math.max(this.pdf.numPages.toString().length, 2) : 2;
        const count = String(value);

        this.uiElements.paginator.current.forEach((el) => {
            el.textContent = count.padStart(padLength, '0');
        })

        this.uiElements.paginator.input.forEach((el) => {
            el.value = count.padStart(padLength, '0');
        })

        this.setPaginatorState();
    }

    private setPaginatorState = () => {
        this.uiElements.paginator.prev.forEach((el) => {
            if (this.currentPage <= 1) el.classList.add('disabled')
            else el.classList.remove('disabled')
        })

        const totalPages = this.pdf.numPages ?? 0;
        const isNextDisabled = !totalPages || (this.currentPage >= totalPages);

        this.uiElements.paginator.next.forEach((el) => {

            if (isNextDisabled) el.classList.add('disabled')
            else el.classList.remove('disabled')
        })
    }

    // ----------------------------------------------------------
    // Navigation — scroll to specific page
    // ----------------------------------------------------------

    goToPage = async (pageNumber: number) => {
        if (!this.pdf) return;
        const target = Math.max(1, Math.min(pageNumber, this.pdf.numPages));
        const entry = this.pageElements.get(target);
        if (!entry) return;

        this.currentPage = target;
        this.updateCurrentPageUiElements(target);
        this.updateActiveThumbnail();

        // programmatic scroll — temporarily disable scroll handler
        this.isScrolling = true;
        entry.wrapper.scrollIntoView({behavior: 'smooth', block: 'start'});

        // re-enable scroll detection after smooth scroll completes
        setTimeout(() => {
            this.isScrolling = false;
        }, 500);
    };

    goToFirst = () => this.goToPage(1);
    goToLast = () => this.goToPage(this.pdf?.numPages ?? 1);
    goToNext = () => this.goToPage(this.currentPage + 1);
    goToPrev = () => this.goToPage(this.currentPage - 1);

    // ----------------------------------------------------------
    // Zoom — rebuild layout with new scale
    // ----------------------------------------------------------
    private adjustZoomPicker = () => {
        if (ReaderUtils.zoom.isNumeric(this.zoomMode)) {
            if (this.zoomMode === this.displayConfig.minZoom) this.uiElements.zoom.out.classList.add('disabled');
            else this.uiElements.zoom.out.classList.remove('disabled');

            if (this.zoomMode === this.displayConfig.maxZoom) this.uiElements.zoom.in.classList.add('disabled');
            else this.uiElements.zoom.in.classList.remove('disabled');

            const hasOption = this.uiElements.zoom.picker.input.querySelector<HTMLOptionElement>('option[value="' + this.zoomMode + '"]')
            if (hasOption) hasOption.selected = true;
        }
    }

    private toggleCustomZoomOption = (render: boolean) => {
        const el = this.uiElements.zoom.picker.input.querySelector<HTMLOptionElement>('option[value="custom"]')

        if (!render) {
            if (el) el.remove();
            return;
        }

        this.uiElements.zoom.picker.input.querySelectorAll<HTMLOptionElement>('option').forEach(el => el.selected = false);

        if (el) {
            el.selected = true;
            return;
        }

        const customOption = document.createElement('option')
        customOption.textContent = 'Custom'
        customOption.value = 'custom';
        customOption.setAttribute('data-display', 'Custom');
        customOption.selected = true;

        this.uiElements.zoom.picker.input.append(customOption)
    }

    private setZoomPickerDisplayOption = () => {
        let hasOption = this.uiElements.zoom.picker.input.querySelector<HTMLOptionElement>(`option[value="${this.uiElements.zoom.picker.input.value}"]`);
        if (!hasOption) hasOption = this.uiElements.zoom.picker.input.querySelectorAll<HTMLOptionElement>('option')[0];
        this.uiElements.zoom.picker.display.textContent = hasOption.getAttribute('data-display');
    }

    setZoom = async (type: ZoomType) => {
        this.zoomMode = type;
        await this.rebuildLayout();
        this.adjustZoomPicker();
        this.setZoomPickerDisplayOption();
    };

    zoomIn = async () => {
        const max = this.displayConfig.maxZoom;
        const base = ReaderUtils.zoom.isNumeric(this.zoomMode) ? (this.zoomMode as number) : this.currentScale;
        const current = Math.min(max, base + this.displayConfig.zoomStep);
        // console.log({current, max, base});
        this.toggleCustomZoomOption(true);
        if (current <= max && this.zoomMode !== current) await this.setZoom(current);
    };

    zoomOut = async () => {

        const min = this.displayConfig.minZoom;
        const base = ReaderUtils.zoom.isNumeric(this.zoomMode) ? (this.zoomMode as number) : this.currentScale;
        const maxBase = Math.max(min, base - this.displayConfig.zoomStep);
        const current = Math.max(maxBase, base - this.displayConfig.zoomStep);
        // console.log({current, min, base});
        this.toggleCustomZoomOption(true);
        if (current >= min && this.zoomMode !== current) await this.setZoom(current);
    };

    rebuildLayout = async () => {
        const previousPage = this.currentPage;
        await this.buildPagesLayout();
        this.setupIntersectionObserver();

        // restore scroll position to current page
        const entry = this.pageElements.get(previousPage);
        if (entry) {
            this.isScrolling = true;
            entry.wrapper.scrollIntoView({block: 'start'});
            setTimeout(() => this.isScrolling = false, 100);
        }
    };

    private computeScale = (page: PDFPageProxy) => {
        const mode = this.zoomMode;
        if (ReaderUtils.zoom.isNumeric(mode)) {
            this.currentScale = mode as number;
            return mode as number;
        }

        const baseViewport = page.getViewport({scale: 1});
        const container = this.uiElements.containers.readingZone;
        const availWidth = container.clientWidth; //-40
        const availHeight = container.clientHeight; //-40

        const widthScale = availWidth / baseViewport.width;
        const heightScale = availHeight / baseViewport.height;

        let scale;
        switch (mode) {
            case 'page-width':
                scale = widthScale;
                break;
            case 'page-fit':
                scale = Math.min(widthScale, heightScale);
                break;
            case 'auto':
            default:
                scale = Math.min(widthScale, 1.25);
                break;
        }

        this.currentScale = scale;
        return scale;
    };

    // ----------------------------------------------------------
    // Search
    // ----------------------------------------------------------
    search = async (query: string) => {
        if (!this.pdf) return;
        const trimmed = query.trim();

        if (!trimmed) {
            this.clearSearch();
            return;
        }

        if (trimmed === this.currentQuery) {
            await this.gotoNextMatch();
            return;
        }

        this.currentQuery = trimmed;
        this.matches = [];
        this.currentMatch = -1;

        const needle = trimmed.toLowerCase();

        for (let pageIndex = 1; pageIndex <= this.pdf.numPages; pageIndex++) {
            const text = await this.getPageText(pageIndex);
            const haystack = text.toLowerCase();
            let from = 0;
            let matchIndex = 0;
            while (true) {
                const idx = haystack.indexOf(needle, from);
                if (idx === -1) break;
                this.matches.push({pageIndex, matchIndex});
                matchIndex += 1;
                from = idx + needle.length;
            }
        }

        // apply highlights to all rendered pages
        this.pageElements.forEach((_, pageIndex) => this.highlightMatchesOnPage(pageIndex));

        if (this.matches.length > 0) await this.gotoNextMatch();
        else this.updateSearchStatus();
    };

    gotoNextMatch = async () => {
        if (this.matches.length === 0) return;
        this.currentMatch = (this.currentMatch + 1) % this.matches.length;
        await this.jumpToCurrentMatch();
    };

    gotoPrevMatch = async () => {
        if (this.matches.length === 0) return;
        this.currentMatch = (this.currentMatch - 1 + this.matches.length) % this.matches.length;
        await this.jumpToCurrentMatch();
    };

    jumpToCurrentMatch = async () => {
        const match = this.matches[this.currentMatch];
        if (!match) return;

        await this.goToPage(match.pageIndex);
        this.updateSearchStatus();

        //TODO: Try to scroll to the match element

        // console.warn(match.pageIndex, this.matches)
        //
        // const entry = this.pageElements.get(match.pageIndex);
        // if (entry && entry.rendered) {
        // console.log('we can scroll')
        // const nodes = entry.textLayer.querySelectorAll('span');
        // const matchElement = nodes[match.pageIndex] ?? null;
        // if (matchElement) {
        //     console.log(matchElement.offsetTop)
        //     const entry = this.pageElements.get(match.pageIndex);
        //
        //     entry.wrapper.scrollTop = matchElement.offsetTop;
        //     matchElement.scrollIntoView({block: 'nearest', behavior: 'smooth'});
        // }
        // }
    };

    getPageText = async (pageIndex: number) => {
        try {
            const cached = this.textCache.get(pageIndex);
            if (cached !== undefined) return cached;
            const page = await this.pdf.getPage(pageIndex);
            const content = await page.getTextContent();
            const text = content.items.map(i => (i as any).str ?? '').join(' ');
            this.textCache.set(pageIndex, text);
            return text;
        } catch (e) {
            this.options.onRenderError(e);
            return '';
        }
    };

    private highlightMatchesOnPage = (pageIndex: number) => {
        if (!this.currentQuery) return;
        const entry = this.pageElements.get(pageIndex);
        if (!entry || !entry.rendered) return;

        const needle = this.currentQuery.toLowerCase();
        const nodes = entry.textLayer.querySelectorAll('span');
        nodes.forEach((node: HTMLElement) => {
            const text = node.textContent ?? '';
            if (text.toLowerCase().includes(needle)) {
                node.classList.add(this.classNames.search.match);
            }
        });
    };

    clearSearch = () => {
        this.matches = [];
        this.currentMatch = -1;
        this.currentQuery = '';
        this.pageElements.forEach(({textLayer}) => {
            textLayer.querySelectorAll(`.${this.classNames.search.match}`).forEach((node) => {
                node.classList.remove(this.classNames.search.match);
            });
        });
        this.updateSearchStatus();
    };

    updateSearchStatus = () => {
        const total = this.matches.length;
        // console.log(total, this.matches.length);

        const disable = (state: boolean) => {
            if (state) {
                this.uiElements.engine.removeAttribute('data-match');
                this.uiElements.search.options.prev.classList.add('disabled');
                this.uiElements.search.options.next.classList.add('disabled');
            } else {
                this.uiElements.search.options.prev.classList.remove('disabled');
                this.uiElements.search.options.next.classList.remove('disabled');
                this.uiElements.engine.setAttribute('data-match', 'true');
            }

        }

        if (this.currentQuery) {
            this.uiElements.engine.setAttribute('data-search', 'filled');
            disable(false);
        } else {
            this.uiElements.engine.removeAttribute('data-search');
            disable(true);
        }

        if (total === 0) {
            const customLabel = this.options?.labels?.emptySearch;
            const resultLabel = (typeof customLabel === 'string' && customLabel.trim().length > 0) ? customLabel : 'Aucun Résultat';
            this.uiElements.search.options.status.textContent = this.currentQuery ? resultLabel : '';
            disable(true);
            return;
        }

        const padLength = Math.max(total.toString().length, 2);
        this.uiElements.search.options.status.textContent = `${String(this.currentMatch + 1).padStart(padLength, '0')} / ${total}`;

    };

    // ----------------------------------------------------------
    // Thumbnails
    // ----------------------------------------------------------
    private renderThumbnails = async () => {
        if (!this.pdf) return;
        this.uiElements.sidebar.scrollable.innerHTML = '';

        const wrapper = document.createElement('div');
        wrapper.className = this.classNames.thumbnail.container;
        this.uiElements.sidebar.scrollable.appendChild(wrapper);

        for (let pageIndex = 1; pageIndex <= this.pdf.numPages; pageIndex++) {
            const thumb = document.createElement('div');
            thumb.className = this.classNames.thumbnail.item;
            thumb.dataset.page = String(pageIndex);

            const canvas = document.createElement('canvas');
            const label = document.createElement('span');
            label.className = this.classNames.thumbnail.pageNumber;
            label.textContent = String(pageIndex);

            thumb.appendChild(canvas);
            thumb.appendChild(label);
            thumb.addEventListener('click', () => this.goToPage(pageIndex));

            wrapper.appendChild(thumb);
            await this.renderThumbnailCanvas(pageIndex, canvas);
        }

        this.updateActiveThumbnail();
    };

    private renderThumbnailCanvas = async (pageIndex: number, canvas: HTMLCanvasElement) => {
        const page = await this.pdf.getPage(pageIndex);
        const baseViewport = page.getViewport({scale: 1});
        const scale = this.displayConfig.thumbnailWidth / baseViewport.width;
        const viewport = page.getViewport({scale});

        const DPR = this.displayConfig.devicePixelRatio;
        const context = canvas.getContext('2d');

        canvas.width = Math.floor(viewport.width * DPR);
        canvas.height = Math.floor(viewport.height * DPR);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        const transform = DPR !== 1 ? [DPR, 0, 0, DPR, 0, 0] : undefined;
        await page.render({canvas, canvasContext: context, viewport, transform}).promise;
    };

    private updateActiveThumbnail = () => {
        const thumbs = this.uiElements.sidebar.container.querySelectorAll<HTMLElement>(`.${this.classNames.thumbnail.item}`);
        thumbs.forEach(thumb => {
            const page = Number(thumb.dataset.page);
            if (page === this.currentPage) {
                thumb.classList.add(this.classNames.thumbnail.current);
                thumb.scrollIntoView({block: 'nearest', behavior: 'smooth'});
            } else {
                thumb.classList.remove(this.classNames.thumbnail.current);
            }
        });
    };

    toggleSidebar = (mode: 'on' | 'off' | 'auto' = 'auto') => {
        if (mode === 'on') this.uiElements.engine.classList.remove('sidebar__off');
        else if (mode === 'off') this.uiElements.engine.classList.add('sidebar__off');
        else this.uiElements.engine.classList.toggle('sidebar__off');
    };

    getSidebarState = () => {
        return this.uiElements.engine.classList.contains('sidebar__off') ? 'off' : 'on';
    };

    // ----------------------------------------------------------
    // Download
    // ----------------------------------------------------------
    private download = () => {
        if (!this.fileBlob) return;
        this.options.onDownload?.(this.fileBlob);
    };

    private renderInfos = () => {
        this.options.onInfosRequired?.();
    };

    setTheme = (mode: Theme = 'auto') => {
        let __theme = mode;

        if (mode === 'auto') {
            const state = AppTheme.cast(localStorage.getItem(this.localStorageKey), true)
            if (state) __theme = state;
            else __theme = AppTheme.get();
        }

        localStorage.setItem(this.localStorageKey, __theme);
        this.root.setAttribute('data-theme', __theme);
    }

    resetTheme = () => {
        localStorage.removeItem(this.localStorageKey);
        this.root.setAttribute('data-theme', AppTheme.get());
    }

    // ----------------------------------------------------------
    // Events
    // ----------------------------------------------------------
    private attachEvents = (): void => {
        // Old listeners Cleanup
        this.abortController?.abort();
        this.abortController = new AbortController();
        const {signal} = this.abortController;

        this.uiElements.paginator.first.forEach((el) => {
            el.addEventListener('click', () => this.goToFirst(), {signal});
        });

        this.uiElements.paginator.last.forEach((el) => {
            el.addEventListener('click', () => this.goToLast(), {signal});
        });

        this.uiElements.paginator.prev.forEach((el) => {
            el.addEventListener('click', () => this.goToPrev(), {signal});
        });

        this.uiElements.paginator.next.forEach((el) => {
            el.addEventListener('click', () => this.goToNext(), {signal});
        });

        document.addEventListener('keyup', (e) => {
            const target = e.target as HTMLElement;
            // Exclude utils & tolbars elements targets
            const isOutsideToolbarsArea =
                target === document.body ||
                target.hasAttribute('data-view') ||
                target.closest('[data-container="toolbar"]') === null
            ;

            if (e.key === 'ArrowLeft' && isOutsideToolbarsArea) this.goToPrev().then(r => r);
            if (e.key === 'ArrowRight' && isOutsideToolbarsArea) this.goToNext().then(r => r);
        }, {signal});

        this.uiElements.paginator.input.forEach((el) => {
            el.addEventListener('change', () => {
                const value = Number(el.value);
                if (Number.isFinite(value)) {
                    this.updateCurrentPageUiElements(value);
                    this.goToPage(value).then(r => r);
                }
            }, {signal});
        });

        // Zoom Handlers
        this.uiElements.zoom.out.addEventListener('click', () => this.zoomOut(), {signal});
        this.uiElements.zoom.in.addEventListener('click', () => this.zoomIn(), {signal});

        this.uiElements.zoom.picker.input.addEventListener('change', () => {
            this.toggleCustomZoomOption(false);

            const value = this.uiElements.zoom.picker.input.value;
            this.setZoom(ReaderUtils.zoom.cast(value)).then(r => r);
        }, {signal});

        window.addEventListener('wheel', (e: Event) => {
            const event = e as WheelEvent;

            // Ctrl on Win/Linux, Cmd (metaKey) on macOS
            if (!event.ctrlKey && !event.metaKey) return;
            event.preventDefault();

            const direction: WheelDirection = event.deltaY < 0 ? 'up' : 'down';
            setTimeout(() => {
                if (direction === 'up') this.zoomIn().then(r => r);
                else this.zoomOut().then(r => r);
            }, 20);
        }, {
            passive: false,
            signal: signal,
        });


        this.uiElements.search.input.input.placeholder = this.options.labels?.searchPlaceholder ?? 'Rechercher ...';
        this.uiElements.search.input.input.addEventListener('keyup', (e) => {
            const value = this.uiElements.search.input.input.value.trim();
            if (value.length > 0) setTimeout(() => this.search(value).then(r => r), 100);
            else if (e.key === 'Enter') this.search(value).then(r => r);
            else if (!value || value.length <= 0) this.clearSearch();
        }, {signal});

        this.uiElements.search.options.next.addEventListener('click', () => this.gotoNextMatch(), {signal});
        this.uiElements.search.options.prev.addEventListener('click', () => this.gotoPrevMatch(), {signal});
        this.uiElements.search.input.toggler.forEach((el) => {
            el.addEventListener('click', () => {
                if (this.uiElements.engine.classList.contains('search:true')) {
                    this.uiElements.engine.classList.remove('search:true')
                    if (el.classList.contains('closer')) {
                        this.uiElements.search.input.input.value = '';
                        this.clearSearch();
                    }
                } else {
                    this.uiElements.search.input.input.focus({preventScroll: true});
                    this.uiElements.engine.classList.add('search:true')
                }
            }, {signal});
        })

        document.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            if (!target.closest('[data-controller="search"]')) {
                if (this.uiElements.search.input.input.value.trim().length <= 0) {
                    this.uiElements.engine.classList.remove('search:true')
                }
            }
        }, {signal})

        this.uiElements.toolbar.downloadCta.forEach((el) => {
            el.addEventListener('click', () => this.download(), {signal});
        });

        if (this.uiElements.toolbar.infosCta) this.uiElements.toolbar.infosCta.forEach((el) => {
            el.addEventListener('click', () => this.renderInfos(), {signal});
        });

        if (this.uiElements.toolbar.homeCta) this.uiElements.toolbar.homeCta.forEach((el) => {
            el.addEventListener('click', () => {
                const maybeLink = el.querySelector('a[href]')
                if (!maybeLink) {
                    if (this.options.onHomeRequired) this.options.onHomeRequired().then(x => x);
                    else this.goToFirst().then(x => x);
                }
            }, {signal});
        });

        this.uiElements.sidebar.toggler.forEach((el) => {
            el.addEventListener('click', () => this.toggleSidebar('auto'), {signal});
        })

        this.uiElements.engine.querySelectorAll('.scrollbable.dismissible').forEach((el) => {
            el.addEventListener('mouseleave', () => setTimeout(() => el.classList.remove('hovered'), 200), {signal});
            el.addEventListener('mouseenter', () => el.classList.add('hovered'), {signal});
        });

        this.uiElements.toolbar.themeToggler.forEach((el) => {
            el.addEventListener('click', () => {
                const currentTheme = AppTheme.current();
                if (currentTheme === 'light') this.setTheme('dark');
                else this.setTheme('light');
            }, {signal});
        })

        window.addEventListener('resize', () => {
            this.responsive();
            this.setZoom('auto').then(x => x);
        }, {signal});

        this.responsive();
    };

    private responsive = (): void => {
        if (window.innerWidth <= 768) {
            this.toggleSidebar('off')
            this.uiElements.engine.classList.add('mobile:true')
        } else {
            if (this.getSidebarState() != 'on') this.toggleSidebar('on')
            this.uiElements.engine.classList.remove('mobile:true')
        }
    };

    // ----------------------------------------------------------
    // Rebind Events Listeners
    // ----------------------------------------------------------
    // noinspection JSUnusedGlobalSymbols
    rebindEvents = (): void => {
        this.attachEvents();
    };

    // ----------------------------------------------------------
    // Destroy Events Listeners
    // ----------------------------------------------------------
    // noinspection JSUnusedGlobalSymbols
    public destroy = (): void => {
        this.abortController?.abort();
        this.abortController = undefined;
    };

    // ----------------------------------------------------------
    // Get UI Elements
    // ----------------------------------------------------------
    // noinspection JSUnusedGlobalSymbols
    getUIElements() {
        return this.uiElements;
    }
}